<template>
  <div class="enter-code">
    <!-- Logo -->
    <div class="logo-wrapper">
      <img v-if="isDark" src="/images/logo-font-white.svg" alt="Yuzu" class="logo" />
      <img v-else src="/images/logo-font-black.svg" alt="Yuzu" class="logo" />
    </div>
    <!-- Heading -->
    <h2 class="enter-code-title">{{ $t('app.auth.enter-code.heading') }}</h2>

    <div>
      <!-- Subtitle -->
      <p v-if="props?.email?.length > 0" class="enter-code-subtitle">
        {{ $t('app.auth.enter-code.subtitle') }}
        <strong>{{ email }}</strong>
      </p>
      <p v-else class="enter-code-subtitle">
        {{ $t('app.auth.enter-code.subtitle-no-email') }}
      </p>
    </div>

    <!-- Code Input Fields -->
    <CodeConfirmation :isLoading="isLoading" @update:modelValue="onCodeUpdate" v-model="codeDigits"></CodeConfirmation>

    <!-- Resend Code Link -->
    <div class="resend-wrapper">
      <button
        type="button"
        class="resend-link"
        :disabled="isResending || resendCountdown > 0"
        @click="handleResendCode"
      >
        {{ $t('app.auth.enter-code.resend-code') }}
        <span v-if="resendCountdown > 0" class="countdown">
          ({{ resendCountdown }}s. {{ $t('app.auth.enter-code.spam') }})
        </span>
      </button>
    </div>

    <div class="btn-group">
      <!-- Back Button -->
      <button class="btn-back"
        @click="goBack"
      >
        <i class="bi bi-arrow-return-left"></i>
      </button>

      <!-- Confirm Button -->
      <button
        class="btn-confirm"
        :disabled="codeDigits.length < 8 || isLoading"
        @click="handleConfirmCode"
      >
        <span v-if="!isLoading">{{ $t('app.auth.enter-code.confirm-button') }}</span>
        <span v-else>
          <i class="bi bi-arrow-repeat"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { authApi } from "@/api/authentification.api";
import CodeConfirmation from "@/components/global/CodeConfirmation.vue";
import { fetchTokenQuery, useSubmitCode } from "@/queries/user.query";
import { useDark } from "@vueuse/core";
import axios from "axios";
import { ref } from "vue";
import { useToast } from "vue-toastification";

const { mutateAsync: submitCode } = useSubmitCode();
const isDark = useDark();
const route = useRoute();
const toast = useToast();
const { t } = useI18n();

const props = defineProps({
  email: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["submitCode", "resend", "back"]);

const codeDigits = ref("");
const isLoading = ref(false);
const isResending = ref(false);
const resendCountdown = ref(180);

const handleConfirmCode = async () => {
  if (codeDigits.value.length < 8 || isLoading.value) return;
  isLoading.value = true;

  const accountId = localStorage.getItem("accountId");
  let body: any = {
    code: codeDigits.value,
  }
  if (accountId) {
    body.accountId = accountId;
  } else {
    body.email = props.email;
  }

  try {
    await submitCode(body, {
      onSuccess: (resp) => {
        emit("submitCode");
      },
      onError: (error) => {
        console.log(error);
        if (axios.isAxiosError(error) && error.response?.status === 429) {
          toast.error(t('app.auth.enter-code.too-many-requests'));
        } else {
          toast.error(t('app.auth.enter-code.wrong-code'));
        }
        codeDigits.value = "";
      }
    });
  } finally {
    isLoading.value = false;
  }
};

const onCodeUpdate = (code: string) => {
  if (code.length === 8) {
    codeDigits.value = code;
    handleConfirmCode();
  }
}

const startCountdown = () => {
  const countdown = setInterval(() => {
    resendCountdown.value--;
    if (resendCountdown.value <= 0) {
      clearInterval(countdown);
      isResending.value = false;
    }
  }, 1000);
}

const handleResendCode = async () => {
  if (isResending.value || resendCountdown.value > 0) return;

  isResending.value = true;
  resendCountdown.value = 180;

  try {
    const invitationId = route.query.invitation_id as string;
    const accountId = localStorage.getItem("accountId");

    if (accountId) {
      await authApi.requestCode(undefined, accountId, !!invitationId);
    } else {
      await authApi.requestCode(props.email, undefined, !!invitationId);
    }

    startCountdown();
  } catch (error) {
    toast.error(t('app.auth.enter-code.error'));
    console.error("Resend error:", error);
    isResending.value = false;
    resendCountdown.value = 0;
  }
};

const goBack = () => {
  emit("back");
};

onMounted(() => {
  startCountdown();
})
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.enter-code {
  display: flex;
  flex-direction: column;
  //align-items: center;
  justify-content: center;

  min-height: 100vh;
  width: 40%;
  max-width: 718px;

  @media screen and (max-width: $medium-screen) {
    width: 85%;
  }

  .logo-wrapper {
    display: flex;
    margin-bottom: 2rem;

    @media screen and (max-width: $medium-screen) {
      justify-content: center;
      margin-bottom: 1rem;
    }

    .logo {
      height: 32px;

      @media screen and (max-width: $medium-screen) {
        height: 25px;
      }
    }
  }

}

.enter-code-title {
  margin-bottom: 0.75rem;
  font-size: 32px;
  font-weight: 600;
  color: var(--secondary-color);

  @media screen and (max-width: $medium-screen) {
    font-size: 20px !important;
    margin-bottom: 1rem;
    text-align: center;
    line-height: normal !important;
  }
}

.enter-code-subtitle {
  font-size: 16px;
  color: var(--secondary-color);
  margin-bottom: 2rem;

  strong {
    font-weight: 600;
  }

  @media screen and (max-width: $medium-screen) {
    font-size: 12px !important;
    margin-bottom: 1rem;
    text-align: center;
  }
}

// .enter-code-spam {
//   font-size: 14px;
//   color: var(--secondary-color);
//   margin-bottom: 2rem;
// }

.resend-wrapper {
  text-align: center;
  margin-bottom: 1.5rem;

  @media screen and (max-width: $medium-screen) {
    margin-bottom: 1rem;
  }
}

.resend-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 16px;
  color: var(--secondary-color);
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: $primary-color;
  }

  &:disabled {
    cursor: not-allowed;
  }

  .countdown {
    margin-left: 0.25rem;
    color: $secondary-300-color;
  }

  @media screen and (max-width: $medium-screen) {
    font-size: 11px;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.btn-group {
  display: flex;
  gap: 8px;
  margin-bottom: 1.25rem;

  .btn-back {
    padding: 1rem;
    font-size: 16px;
    font-weight: 600;
    background-color: var(--light-grey-color);
    border: none;
    border-radius: 4px;

    &:hover:not(:disabled) {
      opacity: 0.7;
    }

    @media screen and (max-width: $medium-screen) {
      font-size: 11px;
      padding: 0.7rem;
    }
  }

  .btn-confirm {
    width: 100%;
    padding: 1rem;
    background-color: $primary-color;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 600;
    color: $secondary-color;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
      background-color: darken($primary-color, 8%);
    }

    &:active:not(:disabled) {
      background-color: darken($primary-color, 16%);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    i {
      animation: spin 1s linear infinite;
    }

    @media screen and (max-width: $medium-screen) {
      font-size: 11px;
      padding: 0.7rem;
    }
  }
}

.wrong-code-wrapper {
  text-align: center;
}

.wrong-code-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  color: $red-color;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: darken($red-color, 8%);
    text-decoration: underline;
  }
}

@media screen and (max-width: $large-screen) {
  .enter-code-title {
    font-size: 32px;
  }

  .enter-code-subtitle {
    font-size: 16px;
  }
}
</style>
